import React, { useState, useEffect } from 'react';

const OrderPrintA4 = ({ order, customer, showItemRate = true }) => {
  const [termsAndConditions, setTermsAndConditions] = useState([]);

  useEffect(() => {
    // Fetch terms and conditions for orders
    const fetchTerms = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/terms-conditions/type/orders`);
        if (response.ok) {
          const data = await response.json();
          // Process terms: split content by lines for bullet points
          const processedTerms = [];
          data.forEach(term => {
            if (term.is_active && term.content) {
              // Split content by newlines and filter out empty lines
              const lines = term.content.split('\n').filter(line => line.trim());
              processedTerms.push(...lines);
            }
          });
          setTermsAndConditions(processedTerms);
        }
      } catch (error) {
        console.error('Error fetching terms:', error);
      }
    };
    fetchTerms();
  }, []);

  if (!order) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Group payments by mode
  const paymentsByMode = {};
  (order.advance_payments || []).forEach(payment => {
    const mode = payment.mode || 'Other';
    if (!paymentsByMode[mode]) paymentsByMode[mode] = [];
    paymentsByMode[mode].push(payment);
  });

  // Calculate mode subtotals
  const calculateModeSubtotal = (payments) => {
    return payments.reduce((sum, p) => {
      if (p.mode === 'Metal Credit') {
        return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
      }
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
  };

  return (
    <div id="a4-print-content" className="print-content-hidden">
      {order?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ 
        fontFamily: "'Roboto', 'Arial', sans-serif",
        fontSize: '11px',
        lineHeight: '1.4',
        color: '#000',
        padding: '20mm',
        maxWidth: '210mm',
        margin: '0 auto',
        backgroundColor: '#fff'
      }}>
        
        {/* Company Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '3px solid #000', paddingBottom: '15px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '2px' }}>
            CUSTOMER ORDER
          </h1>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#d97706', marginBottom: '3px' }}>
            Order ID: {order.order_id}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Date: {formatDate(order.order_date || order.created_at)} | Status: <span style={{ fontWeight: 'bold', color: order.status === 'Completed' ? '#059669' : '#d97706' }}>{order.status}</span>
          </div>
        </div>

        {/* Customer & Rate Info Row */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
          {/* Customer Information */}
          <div style={{ flex: 1, border: '2px solid #000', padding: '12px', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
            <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid #000', paddingBottom: '5px', margin: '0 0 10px 0', textTransform: 'uppercase' }}>
              👤 Customer Information
            </h2>
            {customer && (
              <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                <div style={{ marginBottom: '4px' }}><strong>Name:</strong> {customer.name}</div>
                {customer.mobile && <div style={{ marginBottom: '4px' }}><strong>Mobile:</strong> {customer.mobile}</div>}
                {customer.alternate_mobile && <div style={{ marginBottom: '4px' }}><strong>Alternate Mobile:</strong> {customer.alternate_mobile}</div>}
                {customer.email && <div style={{ marginBottom: '4px' }}><strong>Email:</strong> {customer.email}</div>}
                {customer.address && <div style={{ marginBottom: '4px' }}><strong>Address:</strong> {customer.address}</div>}
              </div>
            )}
          </div>

          {/* Rate Information */}
          {(order.rate_policy || order.base24k_rate_ex_gst) && (
            <div style={{ flex: 1, border: '2px solid #f59e0b', padding: '12px', borderRadius: '6px', backgroundColor: '#fffbeb' }}>
              <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid #f59e0b', paddingBottom: '5px', margin: '0 0 10px 0', textTransform: 'uppercase' }}>
                💰 Rate Information
              </h2>
              <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                {order.rate_policy && (
                  <div style={{ marginBottom: '4px' }}><strong>Rate Policy:</strong> {
                    order.rate_policy === 'lock-today' ? 'Rate Booked Today' : 'Rate at Delivery'
                  }</div>
                )}
                {order.rate_policy === 'lock-today' && order.base24k_rate_ex_gst && (
                  <>
                    <div style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 'bold', color: '#d97706' }}>
                      <strong>24K Base Rate:</strong> ₹{formatCurrency(order.base24k_rate_ex_gst)}/g
                    </div>
                    {order.lock_date && (
                      <div style={{ marginBottom: '4px' }}><strong>Lock Date:</strong> {formatDate(order.lock_date)}</div>
                    )}
                  </>
                )}
                {order.gst_applicable === false ? (
                  <div style={{ marginBottom: '4px' }}><strong>GST:</strong> Kaccha (No GST)</div>
                ) : (
                  <div style={{ marginBottom: '4px' }}><strong>GST:</strong> {order.gst_percent_snapshot || 3}% {order.gst_input_style === 'INCL' ? '(Inclusive)' : '(Exclusive)'}  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Order Items Section */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '12px', borderBottom: '2px solid #000', paddingBottom: '5px', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
            📦 Order Items ({order.items?.length || 0})
          </h2>
          
          {order.items?.map((item, idx) => (
            <div key={idx} style={{ 
              marginBottom: '15px', 
              border: '2px solid #ddd', 
              padding: '12px', 
              borderRadius: '6px',
              backgroundColor: '#fff',
              pageBreakInside: 'avoid'
            }}>
              {/* Item Header */}
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#d97706', borderBottom: '1px solid #ddd', paddingBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Item #{idx + 1}: {item.item_type || 'Item'} ({item.purity || '22K'})</span>
                {item.ready_item && (
                  <span style={{ backgroundColor: '#16a34a', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '900' }}>READY STOCK</span>
                )}
              </div>

              {/* Item Images */}
              {item.photos && item.photos.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  {item.photos.map((photo, photoIdx) => (
                    <img 
                      key={photoIdx}
                      src={photo} 
                      alt={`Item ${idx + 1} Photo ${photoIdx + 1}`}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Item Details Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '8px', 
                fontSize: '10px',
                marginBottom: '8px'
              }}>
                <div style={{ padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <strong>Weight:</strong> {
                    item.weight_type === 'Fixed' ? `${item.weight_fixed}g` : `${item.weight_min}-${item.weight_max}g`
                  }
                </div>
                <div style={{ padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <strong>Quantity:</strong> {item.quantity || 1} piece{parseInt(item.quantity || 1) > 1 ? 's' : ''}
                </div>
                <div style={{ padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <strong>Purity:</strong> {item.purity || 'N/A'}
                </div>
                {item.size && (
                  <div style={{ padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    <strong>Size:</strong> {item.size}
                  </div>
                )}
                {item.making_value && (
                  <div style={{ padding: '6px', backgroundColor: '#fffbeb', borderRadius: '4px' }}>
                    <strong>Making:</strong> {
                      item.making_type === 'PERCENT' ? `${item.making_value}%` :
                      item.making_type === 'PER_GRAM' ? `₹${item.making_value}/gram` :
                      item.making_type === 'WASTAGE' ? `${item.making_value} %Wst` :
                      `₹${item.making_value}`
                    }
                  </div>
                )}
                {showItemRate && item.rate && parseFloat(item.rate) > 0 && order.rate_policy !== 'rate-at-delivery' && (
                  <div style={{ padding: '6px', backgroundColor: '#fffbeb', borderRadius: '4px' }}>
                    <strong>Rate:</strong> ₹{item.rate}/gram
                  </div>
                )}
                {showItemRate && order.rate_policy === 'rate-at-delivery' && (
                  <div style={{ padding: '6px', backgroundColor: '#fffbeb', borderRadius: '4px' }}>
                    <strong>Rate:</strong> At Delivery
                  </div>
                )}
                {item.delivery_date && (
                  <div style={{ padding: '6px', backgroundColor: '#fef3c7', borderRadius: '4px' }}>
                    <strong>Delivery:</strong> {formatDate(item.delivery_date)}
                  </div>
                )}
                {item.tag_number && (
                  <div style={{ padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    <strong>Tag:</strong> {item.tag_number}
                  </div>
                )}
              </div>

              {/* Remarks */}
              {item.public_remark && (
                <div style={{ marginTop: '6px', padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '4px', fontSize: '10px', borderLeft: '3px solid #6366f1' }}>
                  <strong>Customer Remark:</strong> {item.public_remark}
                </div>
              )}

              {item.internal_remark && (
                <div style={{ marginTop: '6px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '10px', borderLeft: '3px solid #f59e0b' }}>
                  <strong>Internal Remark:</strong> {item.internal_remark}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Payments & Deposits Section */}
        {order.advance_payments && order.advance_payments.length > 0 && (
          <div style={{ marginBottom: '20px', pageBreakInside: 'avoid' }}>
            <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '12px', borderBottom: '2px solid #000', paddingBottom: '5px', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
              💳 Payments & Deposits
            </h2>

            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '15px', border: '2px solid #ddd', padding: '10px', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                <h3 style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#059669', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
                  {mode}
                </h3>

                {/* Old Gold - Detailed Breakdown */}
                {mode === 'Old Gold' && paymentsByMode[mode].map((payment, payIdx) => (
                  <div key={payIdx} style={{ marginBottom: '10px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                    <div style={{ fontSize: '10px', marginBottom: '6px' }}>
                      <strong>Date:</strong> {formatDate(payment.date)} | <strong>Amount:</strong> ₹{formatCurrency(payment.amount || 0)}
                    </div>
                    {payment.items && payment.items.length > 0 && (
                      <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #ddd' }}>
                        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>Items:</div>
                        {payment.items.map((item, itemIdx) => (
                          <div key={itemIdx} style={{ fontSize: '9px', marginBottom: '4px', paddingLeft: '10px', borderLeft: '2px solid #ddd' }}>
                            {item.item_name && <div><strong>Item:</strong> {item.item_name}</div>}
                            {item.gross_wt && <div><strong>Gross Weight:</strong> {item.gross_wt}g</div>}
                            {item.less_wt && <div><strong>Less Weight:</strong> {item.less_wt}g</div>}
                            {item.net_wt && <div><strong>Net Weight:</strong> {item.net_wt}g</div>}
                            {item.purity && <div><strong>Purity:</strong> {item.purity}%</div>}
                            {item.fine_wt && <div><strong>Fine Weight:</strong> {item.fine_wt}g</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {payment.note && (
                      <div style={{ marginTop: '6px', fontSize: '9px', fontStyle: 'italic', color: '#666' }}>
                        <strong>Note:</strong> {payment.note}
                      </div>
                    )}
                  </div>
                ))}

                {/* Metal Credit - Detailed Breakdown */}
                {mode === 'Metal Credit' && paymentsByMode[mode]
                  .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
                  .map((payment, payIdx) => {
                    const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
                    
                    return (
                      <div key={payIdx} style={{ marginBottom: '10px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                        {hasDeposits ? (
                          <>
                            {payment.deposits.map((deposit, depIdx) => (
                              <div key={depIdx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: depIdx < payment.deposits.length - 1 ? '1px dashed #ddd' : 'none' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '6px', color: '#059669' }}>
                                  Deposit {depIdx + 1}{deposit.item_name && `: ${deposit.item_name}`}
                                </div>
                                <div style={{ fontSize: '9px', lineHeight: '1.5' }}>
                                  <div><strong>Date:</strong> {formatDate(deposit.date || payment.date)}</div>
                                  {(deposit.type || deposit.type_name_other) && <div><strong>Type:</strong> {deposit.type_name_other || deposit.type}</div>}
                                  {deposit.gross_wt && <div><strong>Gross Weight:</strong> {deposit.gross_wt}g</div>}
                                  {deposit.less_wt && <div><strong>Less Weight:</strong> {deposit.less_wt}g</div>}
                                  {deposit.net_wt && <div><strong>Net Weight:</strong> {deposit.net_wt}g</div>}
                                  {(deposit.purity || deposit.purity_label) && <div><strong>Purity:</strong> {deposit.purity_label || `${deposit.purity}%`}</div>}
                                  <div style={{ fontWeight: 'bold', color: '#059669' }}><strong>Fine Weight:</strong> {deposit.fine_wt || 0}g</div>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div style={{ fontSize: '10px' }}>
                            <div><strong>Date:</strong> {formatDate(payment.date)}</div>
                            <div style={{ fontWeight: 'bold', color: '#059669' }}><strong>Fine Weight:</strong> {payment.fine_wt || payment.fine_grams || 0}g</div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Regular Payments - Table Format */}
                {mode !== 'Old Gold' && mode !== 'Metal Credit' && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#e5e7eb' }}>
                        <th style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'left' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsByMode[mode].map((payment, payIdx) => {
                        const amount = parseFloat(payment.amount) || 0;
                        const isRefund = payment.transaction_type === 'REFUND';
                        const displayAmount = isRefund ? -amount : amount;
                        
                        return (
                          <tr key={payIdx}>
                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                              {formatDate(payment.date)}
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: isRefund ? '#dc2626' : '#059669' }}>
                              {isRefund ? '-' : ''}₹{formatCurrency(Math.abs(displayAmount))}
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                              {isRefund && <div style={{ color: '#dc2626', fontWeight: 'bold' }}>REFUND</div>}
                              {payment.mode === 'UPI' && payment.upi_ref && <div>Ref: {payment.upi_ref}</div>}
                              {payment.mode === 'Card' && payment.card_last4 && <div>Card: ****{payment.card_last4}</div>}
                              {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref && <div>UTR: {payment.utr_ref}</div>}
                              {payment.mode === 'Cheque' && payment.cheque_no && <div>Cheque: #{payment.cheque_no}</div>}
                              {payment.note && <div style={{ fontSize: '9px', color: '#666' }}>{payment.note}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Mode Subtotal */}
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '11px', 
                  fontWeight: 'bold', 
                  textAlign: 'right',
                  paddingTop: '8px',
                  borderTop: '2px solid #059669',
                  color: '#059669'
                }}>
                  {mode} Total: {mode === 'Metal Credit' ? `${calculateModeSubtotal(paymentsByMode[mode])}g` : `₹${formatCurrency(calculateModeSubtotal(paymentsByMode[mode]))}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Signatures Section */}
        {(order.management_signature || order.customer_signature) && (
          <div style={{ marginBottom: '20px', pageBreakInside: 'avoid' }}>
            <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '12px', borderBottom: '2px solid #000', paddingBottom: '5px', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
              ✍️ Signatures
            </h2>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'space-between' }}>
              {order.management_signature && (
                <div style={{ flex: 1, border: '1px solid #ddd', padding: '10px', borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>Management</div>
                  <img 
                    src={order.management_signature} 
                    alt="Management Signature"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '80px',
                      margin: '0 auto',
                      display: 'block',
                      border: '1px solid #ddd'
                    }}
                  />
                  {order.management_signature_name && (
                    <div style={{ fontSize: '10px', marginTop: '5px', color: '#666' }}>
                      {order.management_signature_name}
                    </div>
                  )}
                </div>
              )}

              {order.customer_signature && (
                <div style={{ flex: 1, border: '1px solid #ddd', padding: '10px', borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>Customer</div>
                  <img 
                    src={order.customer_signature} 
                    alt="Customer Signature"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '80px',
                      margin: '0 auto',
                      display: 'block',
                      border: '1px solid #ddd'
                    }}
                  />
                  {customer && customer.name && (
                    <div style={{ fontSize: '10px', marginTop: '5px', color: '#666' }}>
                      {customer.name}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Terms & Conditions */}
        <div style={{ marginTop: '20px', pageBreakInside: 'avoid', border: '2px solid #000', padding: '12px', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid #000', paddingBottom: '5px', margin: '0 0 10px 0', textTransform: 'uppercase' }}>
            📋 Terms & Conditions
          </h2>
          {termsAndConditions.length > 0 ? (
            <div style={{ fontSize: '9px', lineHeight: '1.6' }}>
              {termsAndConditions.map((line, idx) => (
                <div key={idx} style={{ marginBottom: '6px', paddingLeft: '15px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '0' }}>•</span>
                  {line}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '9px', fontStyle: 'italic', color: '#666' }}>
              No terms and conditions configured.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #ddd', textAlign: 'center', fontSize: '9px', color: '#666' }}>
          <div>Thank you for your business!</div>
          <div style={{ marginTop: '5px' }}>This is a computer-generated document.</div>
        </div>

      </div>
    </div>
  );
};

export default OrderPrintA4;
